import { useEffect, useRef, useState, useCallback } from 'react';
import { AgentStreamClient } from '../core/AgentStreamClient';
import type { Block, ConnectionStatus, AgentStreamConfig } from '../core/types';

export interface UseAgentStreamConfig extends AgentStreamConfig {
  autoConnect?: boolean;
}

export interface UseAgentStreamResult {
  blocks: Map<string, Block>;
  status: ConnectionStatus;
  isAgentRunning: boolean;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  reset: () => void;
}

export function useAgentStream(config: UseAgentStreamConfig): UseAgentStreamResult {
  const clientRef = useRef<AgentStreamClient | null>(null);
  const [blocks, setBlocks] = useState<Map<string, Block>>(new Map());
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [isAgentRunning, setIsAgentRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable callback references
  const userOnBlockUpdate = useRef(config.onBlockUpdate);
  const userOnStatusChange = useRef(config.onStatusChange);
  const userOnAgentRunningChange = useRef(config.onAgentRunningChange);
  const userOnError = useRef(config.onError);

  // Update callback refs when they change
  useEffect(() => {
    userOnBlockUpdate.current = config.onBlockUpdate;
    userOnStatusChange.current = config.onStatusChange;
    userOnAgentRunningChange.current = config.onAgentRunningChange;
    userOnError.current = config.onError;
  }, [config.onBlockUpdate, config.onStatusChange, config.onAgentRunningChange, config.onError]);

  // Initialize client (only on mount or when URL/method/body changes)
  useEffect(() => {
    const client = new AgentStreamClient({
      ...config,
      onBlockUpdate: (block) => {
        setBlocks(client.getBlocks());
        userOnBlockUpdate.current?.(block);
      },
      onStatusChange: (newStatus) => {
        setStatus(client.getStatus());
        userOnStatusChange.current?.(newStatus);
      },
      onAgentRunningChange: (running) => {
        setIsAgentRunning(client.isAgentRunning());
        userOnAgentRunningChange.current?.(running);
      },
      onError: (err) => {
        setError(client.getError());
        userOnError.current?.(err);
      },
    });

    clientRef.current = client;

    // Auto-connect if requested with retry logic
    let reconnectTimeout: NodeJS.Timeout | null = null;
    let isCleanedUp = false;

    if (config.autoConnect) {
      const attemptConnect = async () => {
        if (isCleanedUp) return;

        try {
          await client.connect();
        } catch (err) {
          if (isCleanedUp) return;

          // Retry connection after 2 seconds (matching old behavior)
          reconnectTimeout = setTimeout(() => {
            if (!isCleanedUp && clientRef.current === client) {
              attemptConnect();
            }
          }, 2000);
        }
      };
      attemptConnect();
    }

    // Cleanup on unmount or config change
    return () => {
      isCleanedUp = true;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      try {
        client.disconnect();
      } catch (err) {
        // Ignore disconnect errors during cleanup
        console.error('Cleanup disconnect error:', err);
      }
    };
    // NOTE: config.headers is intentionally not in dependencies to prevent reconnection on token refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.url, config.method, config.body, config.autoConnect]);

  // Update headers without reconnecting (for token refresh)
  useEffect(() => {
    if (clientRef.current && config.headers) {
      clientRef.current.updateConfig({ headers: config.headers });
    }
  }, [config.headers]);

  // Stable action functions
  const connect = useCallback(async () => {
    if (clientRef.current) {
      await clientRef.current.connect();
    }
  }, []);

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      try {
        clientRef.current.disconnect();
      } catch (err) {
        // Ignore disconnect errors
        console.error('Disconnect error:', err);
      }
    }
  }, []);

  const reset = useCallback(() => {
    if (clientRef.current) {
      clientRef.current.reset();
      setBlocks(new Map());
      setIsAgentRunning(false);
      setError(null);
    }
  }, []);

  return {
    blocks,
    status,
    isAgentRunning,
    error,
    connect,
    disconnect,
    reset,
  };
}
